import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as planApis from 'apis/plans';
import plan1 from '../../../assets/images/plan1.jpg';
import plan2 from '../../../assets/images/plan2.jpg';
import plan3 from '../../../assets/images/plan3.jpg';
import plan4 from '../../../assets/images/plan4.jpg';
import plan5 from '../../../assets/images/plan5.jpg';
import './PlansListStatic.scss';

/*
  Membership Plans layout. The hero above ("MEMBERSHIP PLANS") is the
  original mockup banner and is left as-is. Everything from "Choose
  Your Plan" down (plan cards + secure payments strip) is ported from
  the plans-list-v2.html static mockup (kept at the repo root for
  design reference/backup) - photo sits only behind the icon/name/price
  zone of each card, features/button stay on a plain black background.

  Self-contained component, all CSS scoped under ".pp-page" with
  "pp-" prefixed classes so nothing here collides with the rest of
  the app's global utility classes (eg. the "pl-*" spacing helpers).

  Wired the same way plans are fetched on the Home page and /Offers
  (GET /v1/plans/active).

  Defaults render the standalone /Plans page. The sections are
  individually switchable so the same cards can be dropped into another
  page as a plain section: Home and the public /Offers page embed it
  with showHero off (those pages have their own banner/carousel) and
  showTrustStrip off.
*/

// Ordered entry -> premium tier icons, one per expected plan (Basic,
// Standard, Premium, Elite). Plans are sorted by price (ascending) and
// matched to this array by position, so the cheapest plan always gets
// the first icon and pricier plans move down the list. If a 5th plan
// ever gets added, it reuses the last (most premium) icon.
const PLAN_ICONS = [
  'fa-solid fa-fire',
  'fa-solid fa-fire-flame-curved',
  'fa-solid fa-gem',
  'fa-solid fa-crown',
];

const iconForRank = (index) => PLAN_ICONS[Math.min(index, PLAN_ICONS.length - 1)];

// Background photo behind each card's icon/name/price zone, one per
// expected plan. The Plan entity (taurian-backend) has no image field
// yet, so these are matched by rank the same way the icons above are -
// swap in a real per-plan `plan.image` here once admin can upload one.
const PLAN_IMAGES = [plan5, plan4, plan3, plan2, plan1];

const imageForRank = (index) => PLAN_IMAGES[Math.min(index, PLAN_IMAGES.length - 1)];

const CYCLE_LABEL = { monthly: 'mo', yearly: 'yr' };

const PlansListStatic = ({
  showHero = true,
  showSecurePayments = true,
  showTrustStrip = true,
  // Off where the visitor is a returning member, who never gets free days.
  showFreePeriod = true,
  // Set by callers that handle the plan themselves instead of sending a
  // visitor to signup, eg. the renewal page.
  onSelectPlan,
  actionLabel,
  disabled = false,
}) => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [selectedCycle, setSelectedCycle] = useState('monthly');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await planApis.fetchPlans();
        setPlans(response.data || []);
      } catch (error) {
        console.log(error);
      }
    };
    fetchData();
  }, []);

  const availableCycles = useMemo(
    () => new Set(plans.map((plan) => plan.billingCycle?.toLowerCase()).filter(Boolean)),
    [plans]
  );
  const hasMonthly = availableCycles.has('monthly');

  // If the API never returns a "monthly" plan, default to whichever
  // cycle is actually available instead of showing an empty grid.
  useEffect(() => {
    if (plans.length === 0) return;
    if (!availableCycles.has(selectedCycle)) {
      setSelectedCycle(hasMonthly ? 'monthly' : [...availableCycles][0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plans]);

  const visiblePlans = useMemo(
    () =>
      plans
        .filter((plan) => plan.billingCycle?.toLowerCase() === selectedCycle)
        .slice()
        .sort((a, b) => Number(a.price) - Number(b.price)),
    [plans, selectedCycle]
  );

  const handleSubscribe = (plan) => {
    if (onSelectPlan) {
      onSelectPlan(plan);
      return;
    }
    if (localStorage.getItem('token')) {
      const role = localStorage.getItem('role');
      navigate(role === 'partner' ? '/partner/verify' : '/dashboard/my-subscription');
      return;
    }
    navigate('/SignUp', { state: { id: plan.id } });
  };

  return (
    <div className="pp-page">

      {/* ===== HERO ===== */}
      {showHero && (
        <div className="pp-hero">
          <div>
            <h1>MEMBERSHIP PLANS</h1>
            <p>Unlock free giveaway entries, exclusive partner benefits and priority access every single month.</p>
          </div>
        </div>
      )}

      <div className="pp-container" id="plans">

        {/* ===== CHOOSE YOUR PLAN (from plans-list-v2.html) ===== */}
        <div className="pp-choose-header">
          <div className="pp-eyebrow"><span className="pp-eyebrow-line"></span><span>Choose Your Plan</span><span className="pp-eyebrow-line right"></span></div>
          <h2>Join The Culture</h2>
          <p>Pick a plan that fuels your passion.</p>
        </div>

        {/* ===== PLAN CARDS ===== */}
        {visiblePlans.length === 0 && <div className="pp-empty">No plans to show right now.</div>}

        {visiblePlans.length > 0 && (
          <div className="pp-plans-list">
            {visiblePlans.map((plan, index) => {
              const isFree = showFreePeriod && plan.isFreePeriodEnabled;
              return (
              <div className="pp-plan-card" key={plan.id}>

                {/* photo sits only behind the icon/name/price zone */}
                <div className="pp-plan-hero">
                  <div className="pp-plan-hero-bg"><img src={imageForRank(index)} alt={plan.name} /></div>
                  <div className="pp-plan-hero-content">
                    <div className="pp-plan-icon-ring"><i className={iconForRank(index)}></i></div>
                    <div className="pp-plan-name">{plan.name}</div>
                    <span className="pp-stripe-accent"></span>
                    <div className="pp-plan-price-row">
                      {isFree && (
                        <s className="pp-plan-price-was"><sup>₹</sup>{Math.trunc(plan.price)}</s>
                      )}
                      <div className="pp-plan-price"><sup>₹</sup>{isFree ? 0 : Math.trunc(plan.price)}</div>
                      <span className="pp-per">/{CYCLE_LABEL[selectedCycle] || selectedCycle}</span>
                    </div>
                    {isFree && plan.freePeriodText && (
                      <div className="pp-free-note">{plan.freePeriodText}</div>
                    )}
                  </div>
                </div>

                {/* entries pill, features & button stay on plain black background */}
                <div className="pp-plan-body">
                  <div className="pp-entries-pill">
                    <i className="fa-solid fa-ticket"></i>
                    <span className="pp-count">{plan.noOfEntries} Free {plan.noOfEntries === 1 ? 'Entry' : 'Entries'}</span>
                    <span className="pp-per-label">/ {CYCLE_LABEL[selectedCycle] || selectedCycle}</span>
                  </div>
                  <div className="pp-plan-divider"></div>
                  <ul className="pp-plan-features">
                    {(plan.features || []).map((feature, featureIndex) => (
                      <li key={featureIndex}><span className="pp-check-badge"><i className="fa-solid fa-check"></i></span>{feature}</li>
                    ))}
                  </ul>
                  <button
                    className="pp-subscribe-btn"
                    onClick={() => handleSubscribe(plan)}
                    disabled={disabled}
                  >
                    {actionLabel || 'Subscribe Now'}<i className="fa-solid fa-arrow-right"></i>
                  </button>
                </div>

              </div>
              );
            })}
          </div>
        )}

        {/* ===== SECURE PAYMENTS (from plans-list-v2.html) ===== */}
        {showSecurePayments && (
          <div className="pp-secure-card">
            <div className="pp-secure-icon-ring"><i className="fa-solid fa-lock"></i></div>
            <div className="pp-eyebrow"><span className="pp-eyebrow-line"></span><span>Secure Payments</span><span className="pp-eyebrow-line right"></span></div>
            <p className="pp-secure-desc">Razorpay powered checkout with bank-grade encryption</p>
            <div className="pp-payment-badges">
              <span className="pp-brand-mark"><i className="fa-solid fa-bolt"></i>Razorpay</span>
              <span className="pp-badge-divider"></span>
              <span className="pp-badge-pci">PCI DSS</span>
              <span className="pp-badge-divider"></span>
              <span className="pp-ssl-mark"><i className="fa-solid fa-lock"></i>256-bit SSL</span>
            </div>
          </div>
        )}

        {/* ===== TRUST STRIP (original 4-item strip, kept alongside the new Secure Payments card above) ===== */}
        {showTrustStrip && (
          <div className="pp-trust-strip">

            <div className="pp-trust-item">
              <i className="fa-solid fa-shield-halved"></i>
              <h4>SECURE PAYMENTS</h4>
              <p>Razorpay powered checkout with bank-grade encryption</p>
            </div>

            <div className="pp-trust-item">
              <i className="fa-solid fa-rotate-left"></i>
              <h4>CANCEL ANYTIME</h4>
              <p>No lock-ins, manage or cancel your plan whenever you like</p>
            </div>

            <div className="pp-trust-item">
              <i className="fa-solid fa-bolt"></i>
              <h4>INSTANT ACTIVATION</h4>
              <p>Entries and benefits unlock the moment you subscribe</p>
            </div>

            <div className="pp-trust-item">
              <i className="fa-solid fa-headset"></i>
              <h4>PRIORITY SUPPORT</h4>
              <p>Dedicated help for all active members, whenever you need</p>
            </div>

          </div>
        )}

      </div>
    </div>
  );
};

export default PlansListStatic;
